import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { createTimeInstance } from '@utils/time';
import { showSuccessBanner } from '@utils/application';
import { createScoreBackfill, previewScoreBackfill, SCOPE_OPTIONS, UNIVERSE_KINDS } from '@services/scoreBackfills';
import { SpecSelect } from './SpecSelect';
import { UniversePicker } from './UniversePicker';
import { DateWindow } from './DateWindow';
import { BackfillPreviewPanel } from './BackfillPreviewPanel';
import { SelectableCard } from './SelectableCard';
import { formatCount, formatUsd, formatWindow, formatScope, formatUniverseKind, formatIndex } from './format';

const FieldRow = styled.div`
  margin-bottom: 28px;
`;

const ScopeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScopeOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error};
  margin-top: 4px;
  margin-bottom: 8px;
`;

const FeedbackText = styled(Body7)`
  color: ${({ theme }) => theme.themeColors.warning};
  display: block;
  margin-top: 8px;
`;

const SectionDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin-top: 24px;
  padding-top: 16px;
  margin-bottom: 16px;
`;

const ConfirmPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 16px;
`;

const PREVIEW_DEBOUNCE_MS = 400;
const RUN_HINT_ID = 'score-backfill-run-hint';
const SCOPE_FULL_DESC_ID = 'score-backfill-scope-full-desc';
const SCOPE_LATEST_DESC_ID = 'score-backfill-scope-latest-desc';

const toISO = (date) => (date ? createTimeInstance(date).format('YYYY-MM-DD') : null);

export const ScoreBackfillForm = ({ specs, universeOptions, formState, onFormChange, onJobCreated }) => {
  const dispatch = useDispatch();

  const { specId, universeKind, indexValue, tickerTags, fromDate, toDate, scope, windowSeededFromLegacy } = formState;

  const setField = (patch) => onFormChange((prev) => ({ ...prev, ...patch }));

  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState(null);
  const [isReestimating, setIsReestimating] = React.useState(false);

  const [createError, setCreateError] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [tickerFeedback, setTickerFeedback] = React.useState(null);

  const maxTickers = universeOptions?.max_tickers || 1000;
  const maxWindowYears = universeOptions?.max_window_years || 15;
  const maxDate = createTimeInstance().toDate();
  const minDate = createTimeInstance(maxDate).subtract(maxWindowYears, 'year').toDate();

  const handleAddTicker = (x) => {
    const symbol = String(x.symbol || x).toUpperCase();
    const exists = tickerTags.some((t) => String(t.symbol || t).toUpperCase() === symbol);
    if (exists) {
      setTickerFeedback(`${symbol} is already in the list.`);
      return;
    }
    if (tickerTags.length >= maxTickers) {
      setTickerFeedback(`Maximum of ${formatCount(maxTickers)} tickers reached.`);
      return;
    }
    setTickerFeedback(null);
    setField({ tickerTags: [...tickerTags, x.symbol ? x : { symbol }] });
  };

  // Batch add from bulk paste. Symbols arrive already upper-cased from the picker.
  const handleAddTickers = (symbols) => {
    let added = 0;
    let duplicates = 0;
    let overCap = 0;
    const next = [...tickerTags];
    symbols.forEach((sym) => {
      const symbol = String(sym).toUpperCase();
      const exists = next.some((t) => String(t.symbol || t).toUpperCase() === symbol);
      if (exists) {
        duplicates += 1;
        return;
      }
      if (next.length >= maxTickers) {
        overCap += 1;
        return;
      }
      next.push({ symbol });
      added += 1;
    });
    if (added > 0) setField({ tickerTags: next });
    setTickerFeedback(null);
    return { added, duplicates, overCap };
  };

  const handleRemoveTicker = (tag) => {
    setField({ tickerTags: tickerTags.filter((t) => t !== tag) });
  };

  const buildUniverse = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return { kind: UNIVERSE_KINDS.WHOLE_CORPUS };
    if (universeKind === UNIVERSE_KINDS.INDEX) return { kind: UNIVERSE_KINDS.INDEX, value: indexValue };
    return { kind: UNIVERSE_KINDS.TICKERS, value: tickerTags.map((t) => t.symbol).filter(Boolean) };
  };

  const universeValid = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return true;
    if (universeKind === UNIVERSE_KINDS.INDEX) return !!indexValue;
    const symbols = tickerTags.map((t) => t.symbol).filter(Boolean);
    return symbols.length >= 1 && symbols.length <= maxTickers;
  };

  const universeSummary = () => {
    if (universeKind === UNIVERSE_KINDS.WHOLE_CORPUS) return formatUniverseKind(universeKind);
    if (universeKind === UNIVERSE_KINDS.INDEX)
      return `${formatUniverseKind(universeKind)} — ${formatIndex(indexValue)}`;
    const n = tickerTags.length;
    return `${formatUniverseKind(universeKind)} — ${n} ticker${n === 1 ? '' : 's'}`;
  };

  // Returns a validation message when the form is not yet submittable, else null.
  const validationMessage = () => {
    if (!specId) return 'Select a score spec.';
    if (!universeValid()) {
      if (universeKind === UNIVERSE_KINDS.INDEX) return 'Select an index.';
      return 'Add at least one ticker.';
    }
    if (!fromDate || !toDate) return 'Choose a start and end date.';
    const from = createTimeInstance(fromDate);
    const to = createTimeInstance(toDate);
    if (from.isAfter(to)) return 'The start date must be on or before the end date.';
    if (to.diff(from, 'year', true) > maxWindowYears) return `The window cannot exceed ${maxWindowYears} years.`;
    return null;
  };

  const invalidMessage = validationMessage();
  const isFormValid = invalidMessage === null;

  const buildBody = () => ({
    score_spec_id: specId,
    universe: buildUniverse(),
    from: toISO(fromDate),
    to: toISO(toDate),
    scope,
  });

  // Debounced live preview. A stale in-flight response cannot overwrite a newer one.
  // Prior stats stay visible while re-estimating (#14) — never blank on every keystroke.
  React.useEffect(() => {
    if (!isFormValid) {
      setPreview(null);
      setIsReestimating(false);
      setPreviewLoading(false);
      setPreviewError(null);
      return undefined;
    }

    const status = { isCanceled: false };
    setPreviewError(null);
    if (preview === null) {
      setPreviewLoading(true);
    } else {
      setIsReestimating(true);
    }

    const timer = setTimeout(async () => {
      const { preview: result, error } = await previewScoreBackfill(buildBody());
      if (status.isCanceled) return;
      setPreviewLoading(false);
      setIsReestimating(false);
      if (result) {
        setPreview(result);
        setPreviewError(null);
      } else {
        setPreviewError(error);
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      status.isCanceled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specId, universeKind, indexValue, tickerTags, fromDate, toDate, scope]);

  // Any form change makes an open confirm panel stale — close it (#9).
  React.useEffect(() => {
    setIsConfirming(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specId, universeKind, indexValue, tickerTags, fromDate, toDate, scope]);

  const canRun =
    isFormValid &&
    !!preview &&
    !preview.over_ceiling &&
    preview.candidate_count > 0 &&
    !previewLoading &&
    !isReestimating &&
    !previewError &&
    !isSubmitting;

  const runBlockerMessage = () => {
    if (previewLoading) return 'Waiting for the cost estimate…';
    if (isReestimating) return 'Updating the cost estimate…';
    if (previewError) return 'Can’t estimate cost — see the error above.';
    if (preview && preview.over_ceiling) return 'Estimated cost is over the cap — narrow the run.';
    if (preview && preview.candidate_count === 0)
      return 'Nothing matches this spec, universe, and window — widen the window or universe.';
    return invalidMessage;
  };

  // First click opens the confirm panel; the actual POST happens in handleConfirm (#9).
  // Not a native form submit — the shared button components render <button> without a `type`,
  // so any of them inside a <form> would submit it; we drive the flow with explicit onClick.
  const handleStartClick = () => {
    if (!canRun) return;
    setCreateError(null);
    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    setCreateError(null);
    setIsSubmitting(true);
    const { job, error } = await createScoreBackfill(buildBody());
    setIsSubmitting(false);

    if (job) {
      showSuccessBanner('Backfill started', null, dispatch);
      setIsConfirming(false);
      onJobCreated(job);
    } else {
      setCreateError(error);
    }
  };

  const tickerCount = universeKind === UNIVERSE_KINDS.TICKERS ? tickerTags.length : undefined;

  // The spec comes first and alone. Everything after it — universe, window, scope, the estimate —
  // describes a run OF that spec, so it appears once there is one and goes away if the spec is
  // cleared. The hidden fields keep their values: they are owned by the page, not by this render.
  const hasSpec = !!specId;

  return (
    <div>
      <FieldRow>
        <Container bottom={4}>
          <Body5 bold>Score spec</Body5>
        </Container>
        <Container bottom={12}>
          <Body7 isLowContrast>The scoring model to run against each matching document.</Body7>
        </Container>
        <SpecSelect specs={specs} value={specId} onChange={(id) => setField({ specId: id })} />
        {!hasSpec && (
          <Container top={8}>
            <Body7 isLowContrast>Choose a spec to set up the universe, window, and scope for the run.</Body7>
          </Container>
        )}
      </FieldRow>

      {hasSpec && (
        <>
          <FieldRow>
            <Container bottom={12}>
              <Body5 bold>Universe</Body5>
            </Container>
            <UniversePicker
              kind={universeKind}
              onKindChange={(k) => setField({ universeKind: k })}
              indexValue={indexValue}
              onIndexChange={(v) => setField({ indexValue: v })}
              tickerTags={tickerTags}
              onAddTicker={handleAddTicker}
              onAddTickers={handleAddTickers}
              onRemoveTicker={handleRemoveTicker}
              universeOptions={universeOptions}
            />
            {tickerFeedback && universeKind === UNIVERSE_KINDS.TICKERS && <FeedbackText>{tickerFeedback}</FeedbackText>}
          </FieldRow>

          <FieldRow>
            <Container bottom={4}>
              <Body5 bold>Knowable window</Body5>
            </Container>
            <Container bottom={12}>
              {/* The window bounds each occasion's utc_knowable_at — its trigger time, as UTC days — for
              every spec kind (ai_copilot#592). The same clock as the feed's knowable window, and the
              same name, so an analyst who reads a feed back from a run sees one vocabulary. */}
              <Body7 isLowContrast>
                Bounds occasions by when they became knowable, in UTC days. Each occasion whose trigger lands in this
                range is scored once. Same clock as the feed’s knowable window.
              </Body7>
            </Container>
            <DateWindow
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(d) => setField({ fromDate: d, windowSeededFromLegacy: false })}
              onToChange={(d) => setField({ toDate: d, windowSeededFromLegacy: false })}
              maxDate={maxDate}
              minDate={minDate}
              maxWindowYears={maxWindowYears}
              legend="Knowable window"
            />
            {/* Set by "Start a similar run" off a job created before the switch (see isLegacyWindow in
            ./format). Its dates bounded document dates; carried over unchanged they now bound a
            different column, so the run can select a different set. Any edit to either date clears
            it — the dates are then the analyst's, not the old run's. */}
            {windowSeededFromLegacy && (
              <FeedbackText>
                These dates came from a run whose window bounded document dates. This run bounds when occasions became
                knowable, so the same dates may select different occasions.
              </FeedbackText>
            )}
          </FieldRow>

          <FieldRow>
            <Container bottom={12}>
              <Body5 bold>Scope</Body5>
            </Container>
            <ScopeOptions role="radiogroup" aria-label="Scope">
              <SelectableCard selected={scope === SCOPE_OPTIONS.FULL_HISTORY}>
                <input
                  type="radio"
                  name="scope"
                  checked={scope === SCOPE_OPTIONS.FULL_HISTORY}
                  onChange={() => setField({ scope: SCOPE_OPTIONS.FULL_HISTORY })}
                  aria-describedby={SCOPE_FULL_DESC_ID}
                />
                <ScopeOptionText>
                  <Body5 bold>Full history</Body5>
                  <Body7 id={SCOPE_FULL_DESC_ID} isLowContrast>
                    Score every matching document — a company that reported N times scores N times. Best for building a
                    time-series panel.
                  </Body7>
                </ScopeOptionText>
              </SelectableCard>
              <SelectableCard selected={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY}>
                <input
                  type="radio"
                  name="scope"
                  checked={scope === SCOPE_OPTIONS.LATEST_PER_COMPANY}
                  onChange={() => setField({ scope: SCOPE_OPTIONS.LATEST_PER_COMPANY })}
                  aria-describedby={SCOPE_LATEST_DESC_ID}
                />
                <ScopeOptionText>
                  <Body5 bold>Latest per company</Body5>
                  <Body7 id={SCOPE_LATEST_DESC_ID} isLowContrast>
                    One score per company from its most recent matching document — a snapshot as of now, far fewer
                    candidates.
                  </Body7>
                </ScopeOptionText>
              </SelectableCard>
            </ScopeOptions>
          </FieldRow>

          <SectionDivider>
            <Body5 bold>Review &amp; launch</Body5>
          </SectionDivider>

          <BackfillPreviewPanel
            preview={preview}
            isLoading={previewLoading}
            isReestimating={isReestimating}
            error={previewError}
            scope={scope}
            tickerCount={tickerCount}
          />

          {isConfirming ? (
            <Container top={12}>
              <ConfirmPanel>
                <Container bottom={12}>
                  <Body5 bold>Confirm backfill</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Candidates</Body7>
                  <Body5>{formatCount(preview?.candidate_count)}</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Estimated cost</Body7>
                  <Body5>{formatUsd(preview?.cost_estimate_usd)}</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Window</Body7>
                  <Body5>{formatWindow(preview?.resolved_from, preview?.resolved_to)}</Body5>
                </Container>
                <Container row spaceBetween bottom={6}>
                  <Body7 isLowContrast>Universe</Body7>
                  <Body5>{universeSummary()}</Body5>
                </Container>
                <Container row spaceBetween bottom={12}>
                  <Body7 isLowContrast>Scope</Body7>
                  <Body5>{formatScope(scope)}</Body5>
                </Container>
                {createError && <ErrorText>{createError}</ErrorText>}
                <Container row spaceBetween verticallyCenter>
                  <TransparentFlatButton type="button" onClick={() => setIsConfirming(false)}>
                    Back
                  </TransparentFlatButton>
                  <FlatButton type="button" onClick={handleConfirm} disabled={isSubmitting}>
                    {isSubmitting ? 'Starting…' : 'Start backfill'}
                  </FlatButton>
                </Container>
              </ConfirmPanel>
            </Container>
          ) : (
            <Container top={12}>
              {createError && <ErrorText>{createError}</ErrorText>}
              <FlatButton
                onClick={handleStartClick}
                aria-disabled={!canRun}
                aria-describedby={!canRun ? RUN_HINT_ID : undefined}
                fullWidth
              >
                {isSubmitting ? 'Starting…' : 'Start backfill'}
              </FlatButton>
              {!canRun && runBlockerMessage() && (
                <Container top={8}>
                  <Body7 id={RUN_HINT_ID} isLowContrast>
                    {runBlockerMessage()}
                  </Body7>
                </Container>
              )}
            </Container>
          )}
        </>
      )}
    </div>
  );
};
